import {useMemo, useState, useEffect} from "react";
import {GridColDef, GridFilterModel, GridSortModel, GRID_CHECKBOX_SELECTION_COL_DEF} from "@mui/x-data-grid-pro";
import {useGetCompletedCourses, CompletedCourseDto} from "@/app2/shared/data";
import {useTeamProgressContext} from "../../context/TeamProgressContext";
import {useManageTeamProgressReport} from "../../hooks";
import {useNavigate} from "react-router-dom";
import {AdminTable} from "@/app2/shared/components/AdminTable";
import {useTeamProgressColumns, formatDuration} from "../../utils/columns";
import {gridFilterModelToApiFilters} from "../../utils/filters";
import dayjs from "dayjs";
import type {GetCohortSettingsResponse} from "@/app2/shared/data/gen";
import useCustomDataGrid from "@/app2/shared/hooks/useCustomDataGrid";

interface CompletedCoursesDataGridProps {
	organization?: GetCohortSettingsResponse;
}

/**
 * Completed Courses Data Grid
 * Matches legacy implementation exactly
 */
export function CompletedCoursesDataGrid({organization}: CompletedCoursesDataGridProps) {
	const {filters, updateSectionState} = useTeamProgressContext();
	const navigate = useNavigate();
	const commonColumns = useTeamProgressColumns(organization);

	const {
		api: {
			assignFilters,
		}} = useCustomDataGrid<CompletedCourseDto>({});		
	
	const [sortModel, setSortModel] = useState<GridSortModel>([
		{field: "completedOn", sort: "desc"}
	]);
	const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });	

	// Export functionality
	const {initDownload, isExporting} = useManageTeamProgressReport<CompletedCourseDto>("completedCourses", filterModel, sortModel);

	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(25);

	const handleFilterModelChange = (newFilterModel: GridFilterModel) => setFilterModel(newFilterModel);

	// Reset page to 0 when filters change
	useEffect(() => {
		setPage(0);
	}, [filters.dateFrom, filters.dateTo, filters.archivedUsers]);
	useEffect(() => {
		setPage(0);
	}, [filterModel]);

	const {data, isLoading} = useGetCompletedCourses({
		dateFrom: filters.dateFrom,
		dateTo: filters.dateTo,
		archivedUsers: filters.archivedUsers,
		page: page + 1,
		pageSize: pageSize,
		order: sortModel[0]?.sort || "desc",
		orderBy: sortModel[0]?.field || "completedOn",
		filters: gridFilterModelToApiFilters(filterModel),
	});

	useEffect(() => {
		updateSectionState(isLoading, "dataGrid");
	}, [isLoading, updateSectionState]);

	const columns: GridColDef<CompletedCourseDto>[] = useMemo(
		() => {
			const getColumns = () => {
				const baseColumns: GridColDef<CompletedCourseDto>[] = [
					{
						...GRID_CHECKBOX_SELECTION_COL_DEF,
						type: "string" as const,
						hideable: false
					},
					{
						field: "firstName",
						headerName: "First Name",
						flex: 1,
						type: "string" as const
					},
					{
						field: "lastName",
						headerName: "Last Name",
						flex: 1,
						type: "string" as const
					},
					{
						field: "course",
						headerName: "Course",
						flex: 1,
						type: "string" as const
					},
					{
						field: "duration",
						headerName: "Duration",
						flex: 1,
						type: "number" as const,
						valueGetter: (params) => params.row.duration ? formatDuration(params.row.duration) : "",
						filterable: false
					},
					commonColumns.supervisor,
					commonColumns.division,
					{
						field: "completedOn",
						headerName: "Completed On",
						flex: 1,
						type: "date" as const,
						valueGetter: (params) => params.row.completedOn ? new Date(params.row.completedOn) : null
					},
					commonColumns.enrollmentType,
					{
						field: "contentType",
						headerName: "Content Type",
						flex: 1,
						type: "string" as const
					},
					commonColumns.email,
					commonColumns.phone,
					commonColumns.status,
					commonColumns.jobCategory,
					commonColumns.streak,
					commonColumns.jobTitle,
					commonColumns.employeeCode,
					{
						field: "lastActive",
						headerName: "Last Active",
						flex: 1,
						type: "date" as const,
						valueGetter: (params) => params.row.lastActive ? new Date(params.row.lastActive) : null
					}
				];

				// Add custom field columns from commonColumns when enabled
				if (organization?.isActiveCustomField1) baseColumns.push(commonColumns.customFieldValue1);
				if (organization?.isActiveCustomField2) baseColumns.push(commonColumns.customFieldValue2);
				if (organization?.isActiveCustomField3) baseColumns.push(commonColumns.customFieldValue3);

				return baseColumns;
			};

			return getColumns();
		},
		[commonColumns, organization]
	);

	const getFilterDescription = (): string => {
		const {dateRangeType, dateFrom, dateTo} = filters;

		switch (dateRangeType) {
			case "lastWeek":
				return "VIEWING STATS FROM THE PAST WEEK";
			case "lastMonth":
				return "VIEWING STATS FROM THE PAST MONTH";
			case "lastYear":
				return "VIEWING STATS FROM THE PAST YEAR";
			case "customDateRange": {
				const fromDate = dayjs(dateFrom).format("MM/DD/YYYY");
				const toDate = dayjs(dateTo).format("MM/DD/YYYY");
				return `VIEWING STATS ${fromDate} - ${toDate}`;
			}
			default:
				return "VIEWING STATS FROM ALL TIME";
		}
	};

	const handleRowClick = (row: CompletedCourseDto) => {
		navigate(`/admin/users/${row.userId}`);
	};

	return (
		<AdminTable
			rows={data?.data || []}
			columns={columns.map(e => assignFilters(e as any))}
			totalCount={data?.totalCount || 0}
			loading={isLoading}
			page={page}
			pageSize={pageSize}
			pageSizeOptions={[10, 25, 50, 100, 200]}
			onPageChange={setPage}
			onPageSizeChange={setPageSize}
			sortModel={sortModel}
			onSortModelChange={setSortModel}
			filterModel={filterModel}
			onFilterModelChange={handleFilterModelChange}
			onRowClick={handleRowClick}
			getRowId={(row) => `${row.userId}-${row.course}-${row.completedOn}`}
			toolbarText={getFilterDescription()}
			checkboxSelection={false}
			enableAdvancedFilters={true}
			enableColumnFilter={true}
			disableColumnResize={false}
			autoHeight
			initialColumnVisibility={{
				contentType: false,
				email: false,
				phone: false,
				status: false,
				jobCategory: false,
				streak: false,
				jobTitle: false,
				lastActive: false,
				customFieldValue1: false,
				customFieldValue2: false,
				customFieldValue3: false
			}}
			onExport={initDownload}
			exportDisabled={isExporting}
			exportButtonText={isExporting ? "Exporting..." : "Export"}
		/>
	);
}
